import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { clientConfig } from 'src/client-config';
import { AppUserDto, EnterpriseUserDataDto, GainsightPXService, GainsightUserSettings, PIIMode, SchemaTargetDto, SchemaType, SpendUserType, UserRole } from '../services/api.services';
import { catchAppError, getProductDescriptor, isNullOrUndefined } from '../shared/utils';
import { SchemaUtils } from '../shared/utils/schema-utils';
import { isCanvasRendererHost } from '../canvas-renderer/canvas-renderer-host';
import { getInsightsPbiChartType } from '../shared/chart/chart';
import { logger } from '../pbir/utils/logger';

const LOGGER = logger('GainsightPX');

function aptrinsic() {
  return (window as any).aptrinsic;
}

@Injectable({ providedIn: 'root' })
export class GainsightPX {
  enabled = false;

  private settings: GainsightUserSettings;

  constructor(private readonly gainsightPXService: GainsightPXService) { }

  init() {
    if (isCanvasRendererHost)
      return of(null);

    return this.gainsightPXService.getSettings().pipe(
      tap(x => {
        if (x.enabled) {
          this.settings = x;

          if (x.tagScript) {
            (0, eval)(x.tagScript);
            this.enabled = true;
          }
          else {
            LOGGER.error('GainsightPX is enabled, but tagScript is missing.');
          }
        } else {
          this.settings = null;
        }
      }),
      catchAppError(err => {
        LOGGER.error('GainightPX init failed', err);
        return of(null);
      }, { showErrorSnack: false })
    );
  }

  setGlobals() {
    if (isCanvasRendererHost)
      return;

    try {
      if (!this.enabled)
        return;

      aptrinsic()(
        'set',
        'globalContext',
        {
          environmentName: clientConfig.deploymentInfo.environmentName,
          releaseId: clientConfig.deploymentInfo.releaseId
        });
    }
    catch (err) {
      LOGGER.error('GainightPX identify error', err);
    }
  }

  setUser(user: AppUserDto, userClaims: Record<string, string[]>, enterpriseUserData: EnterpriseUserDataDto) {
    if (isCanvasRendererHost)
      return;

    try {
      if (!this.enabled)
        return;
             
      let organisation = userClaims.organisation?.length ? userClaims.organisation[0] : null;
      let organisationId = organisation ? `${user.region}:${organisation}` : null;
      let departmentNames = enterpriseUserData?.spend?.departmentNames;

      let userData = {
        id: user.id,
        insights_region: user.region,
        
	      insights_user_id: user.id,
	      insights_user_role: getUserRoleName(user.role),
	
	      insights_user_locale: user.settings?.locale,
	      insights_user_currency: user.settings?.currency,
	      insights_user_tz: user.settings?.timeZoneIana,
	
	      insights_spend_departmentName: departmentNames?.length ? departmentNames[0] : null,
	      insights_spend_departmentNames: departmentNames?.join('\n'),
	      insights_spend_employeeCode: enterpriseUserData?.spend?.employeeCode,
	      insights_spend_userType: 
          !isNullOrUndefined(enterpriseUserData?.spend?.userType) 
            ? SpendUserType[enterpriseUserData?.spend?.userType]
            : null,
      };

      if (this.settings.includePII != PIIMode.Exclude) {
        let pii = {
          email:     user.username,
          firstName: userClaims.name ? userClaims.name[0] : null,
          lastName:  userClaims.family_name ? userClaims.family_name[0] : null,
        };

        if (this.settings.includePII == PIIMode.IncludeObfuscated) {
          for (let [k, v] of Object.entries(pii)) {
            if (k === 'email') {
              let split = v.split('@');
              pii[k] = `${obfuscate(split[0])}@${split[1]}`;
            }
            else {
              pii[k] = obfuscate(v);
            }
          }

          function obfuscate(value: string) {
            if (value.length < 2)
              return '******';

            return value.split(/\s+/gi).map(x => {
              return x[0] + x.substring(1).replace(/[^@\.]/gi, m => '*'.repeat(Math.max(3, m.length * 1.3)));
            }).join(' ');
          }
        }

        userData = {
          ...userData,
          ...pii
        };
      }

      aptrinsic()(
        'identify',
        userData,
        {
          id:   organisationId,
          name: user.organisationDisplayName ?? organisation,

          insights_region: user.region,

          insights_org:       organisation,
          insights_org_id:    organisationId,
          insights_org_title: user.organisationDisplayName ?? organisation,
        });
    }
    catch (err) {
      LOGGER.error('GainightPX identify error', err);
    }
  }

  recordEvent(eventName: string, properties: Record<string, any>) {
    if (isCanvasRendererHost)
      return;

    try {
      if (!this.enabled)
        return;

      LOGGER.debug(`[GainsightPX] recordEvent '${eventName}'`, properties);
      aptrinsic()(
        'track',
        eventName,
        properties);
    }
    catch (err) {
      LOGGER.error('GainightPX track error', err);
    }
  }

  recordPageView(properties: {
    title: string;
    path: string;
  }) {
    try {
      if (!this.enabled)
        return;

      LOGGER.debug(`[GainsightPX] pageView`, properties);
      aptrinsic()("pageView", properties);
    }
    catch (err) {
      LOGGER.error('GainightPX pageView error', err);
    }
  }

  recordDashboardEvent(name: DashboardEvent, properties: {
    context: ViewContentContext;
    id: number;
    name: string;
    owner: string;
    shared: boolean;
    fromLibrary: boolean;
  }) {
    this.recordEvent(name, {
      context: properties.context,
      dashboadId: properties.id,
      dashboadName: properties.name,
      owner: properties.owner,
      shared: properties.shared,
      fromLibrary: properties.fromLibrary,
    });
  }

  recordVisualEvent(name: VisualEvent, properties: {
    context: ViewContentContext;
    id: number;
    name: string;
    owner: string;
    type: string;
    shared: boolean;
    fromLibrary: boolean;
    dataset: SchemaTargetDto;
  }) {
    this.recordEvent(name, {
      context: properties.context,
      visualId: properties.id,
      visualName: properties.name,
      owner: properties.owner,
      chartType: getInsightsPbiChartType(properties.type)?.title ?? `<${properties.type}>`,
      shared: properties.shared,
      fromLibrary: properties.fromLibrary,

      dataset: properties.dataset.name,

      datasetType:
        properties.dataset.type == SchemaType.Core        ? 'CoreDataset' :
        properties.dataset.type == SchemaType.UserDataset ? 'ExtendedDataset' : null,

      coreDatasetProduct:
        SchemaUtils.isCore(properties.dataset)
          ? getProductDescriptor(properties.dataset.product).title
          : null,

      extendedDatasetId:
        SchemaUtils.isUserDataset(properties.dataset)
          ? properties.dataset.userDatasetId
          : null
    });
  }
}

export enum DashboardEvent {
  ViewDashboard = 'View Dashboard',
  CopyDashboard = 'Copy Dashboard',
  DeleteDashboard = 'Delete Dashboard',
  ExportDashboard = 'Export Dashboard',
}

export enum VisualEvent {
  ViewVisual = 'View Visual',
  CopyVisual = 'Copy Visual',
  DeleteVisual = 'Delete Visual',
  ExportVisual = 'Export Visual',
}

export enum ViewContentContext {
  InsightsLibrary = 'insightsLibrary',
  EditMode = 'editMode',
  ViewMode = 'viewMode',
  Home = 'home',
}

function getUserRoleName(role: UserRole) {
  switch (role) {
    case UserRole.ClientUser:
      return 'Business User';

    case UserRole.InsightsUser:
      return 'Admin';

    case UserRole.ClientAdmin:
      return 'Support Admin';

    case UserRole.InsightsAdmin:
      return 'Super Admin';

    case UserRole.Developer:
      return 'Developer';
  }

  return 'Unknown';
}
